import React, { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from 'react-query';
import Masonry from 'react-masonry-css';
import api from '../config/api';
import ProfileCard from '../components/ProfileCard';
import { Tag, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';

const TagSearch = () => {
  const { tag } = useParams();
  const [params] = useSearchParams();
  const q = (params.get('q') || '').trim();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['profiles', 'tag', tag, q],
    async ({ pageParam = 1 }) => {
      const response = await api.get('/api/profiles', {
        params: {
          page: pageParam,
          limit: 24,
          tags: tag,
          ...(q ? { q } : {})
        }
      });
      return response.data;
    },
    {
      getNextPageParam: (lastPage, pages) => {
        const p = lastPage?.pagination || {};
        const page = Number(p.page) || pages.length || 1;
        const limit = Number(p.limit) || 24;
        const total = Number(p.total) || 0;
        return page * limit < total ? page + 1 : undefined;
      },
      staleTime: 5 * 60 * 1000,
    }
  );

  const profiles = useMemo(
    () => (data?.pages || []).flatMap((p) => (Array.isArray(p?.profiles) ? p.profiles : [])),
    [data]
  );

  const breakpointColumns = {
    default: 4,
    1400: 3,
    1100: 3,
    700: 2,
    500: 1
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-secondary-600">Загрузка профилей с тегом #{tag}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😔</div>
          <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
            Что-то пошло не так
          </h2>
          <p className="text-secondary-600 mb-4">
            Не удалось загрузить профили с тегом #{tag}. Попробуйте обновить страницу.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <SEO
        title={`#${tag} — Тег`}
        description={`Профили с тегом #${tag} на ${BRAND.name}.`}
        url={`${BRAND.baseUrl}/tag/${encodeURIComponent(tag)}`}
        canonical={`${BRAND.baseUrl}/tag/${encodeURIComponent(tag)}`}
        type="website"
      />
      {/* Header */}
      <div className="hero-gradient text-white shadow-soft relative">
        {/* Glassmorphism background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="glass-bg"></div>
          <div className="glass-bg"></div>
          <div className="glass-bg"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
          <div className="glass-card p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Tag className="w-8 h-8 text-white floating" />
              <h1 className="text-3xl font-bold text-white">
                #{tag}
              </h1>
            </div>
            <p className="text-white/90">
              {profiles.length} {profiles.length === 1 ? 'профиль' : profiles.length < 5 ? 'профиля' : 'профилей'} с тегом #{tag}
            </p>
          </div>
        </div>
      </div>

      {/* Profiles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {profiles.length > 0 ? (
          <>
            <Masonry
              breakpointCols={breakpointColumns}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {profiles.map((p) => (
                <div key={p.id} className="mb-6">
                  <ProfileCard profile={p} />
                </div>
              ))}
            </Masonry>

            {/* Infinite Scroll Trigger */}
            {hasNextPage && (
              <div className="flex items-center justify-center py-8">
                {isFetchingNextPage ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
                    <span className="text-secondary-600">Загрузка еще профилей...</span>
                  </div>
                ) : (
                  <button
                    onClick={() => fetchNextPage()}
                    className="btn btn-primary"
                  >
                    Загрузить еще
                  </button>
                )}
              </div>
            )}

            {!hasNextPage && profiles.length > 0 && (
              <div className="text-center py-8">
                <p className="text-secondary-500">
                  Вы просмотрели все профили по этому запросу 🎉
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏷️</div>
            <h3 className="text-xl font-semibold text-secondary-900 mb-2">
              Пока нет профилей с тегом #{tag}
            </h3>
            <p className="text-secondary-600 mb-4">
              Попробуйте другой тег или измените фильтры.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TagSearch;
